import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import css from "./lesson.module.css";
import { getCSRFToken } from "../../lib/csrf";
import { FaVolumeUp } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { trackLessonOpened } from "../../lib/analytics";
import { AiOutlineClose } from "react-icons/ai";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { useRef } from "react";
import addInWords from "../../img/addInWords.mp3";
import VocabularyPopup from "./VocabularyPopup";
const WordsComponent = ({ lesson, onActivityComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showTranscription, setShowTranscription] = useState(true);
  const [selectedWord, setSelectedWord] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [voices, setVoices] = useState([]);
  const [wordExplanation, setWordExplanation] = useState(null);
  const [wordTranslation, setWordTranslation] = useState(null);
  const [wordExample, setWordExample] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [popupError, setPopupError] = useState(null);
  const [translationMap, setTranslationMap] = useState({});
  const [studentVocabularyEntries, setStudentVocabularyEntries] = useState([]);
  const [wordType, setWordType] = useState(null);
  const [wordDetails, setWordDetails] = useState(null);
  const [commonMisunderstandings, setCommonMisunderstandings] = useState(null);
  const hoverTimeout = useRef(null);
  const hoveredWord = useRef(null);
  const [delayedHoveredWord, setDelayedHoveredWord] = useState(null);
  const addSound = new Audio(addInWords);
  const [wordCount, setWordCount] = useState(0);
  const [showPlusOne, setShowPlusOne] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });

  // Translation cache and request management
  const translationCache = useRef(new Map()); // In-memory cache for current session
  const activeRequests = useRef(new Map()); // Track active API requests
  const requestQueue = useRef(new Map()); // Queue callbacks for same word requests

  const { t, i18n } = useTranslation();
  const speechSynth =
    typeof window !== "undefined" ? window.speechSynthesis : null;

  useEffect(() => {
    if (!speechSynth) {
      console.error("Speech Synthesis API не підтримується у цьому браузері");
      return;
    }

    const loadVoices = () => {
      const availableVoices = speechSynth.getVoices();
      if (availableVoices.length > 0) {
        setVoices(availableVoices);
      } else {
        setTimeout(loadVoices, 500);
      }
    };

    loadVoices();
    if (speechSynth.onvoiceschanged !== undefined) {
      speechSynth.onvoiceschanged = loadVoices;
    }
  }, []);
  const getCachedTranslation = (word) => {
    const currentLanguage = i18n.language || 'en';
    const cacheKey = `translation_${word}_${currentLanguage}`;
    const cached = sessionStorage.getItem(cacheKey);
    return cached ? JSON.parse(cached) : null;
  };

  const setCachedTranslation = (word, data) => {
    try {
      const currentLanguage = i18n.language || 'en';
      const cacheKey = `translation_${word}_${currentLanguage}`;
      const serializedData = JSON.stringify(data);
      sessionStorage.setItem(cacheKey, serializedData);
      console.log(`💾 [SESSION STORAGE] Cached "${word}" for ${currentLanguage} (${serializedData.length} bytes)`);
    } catch (error) {
      if (error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
        console.warn(`⚠️ [SESSION STORAGE] Quota exceeded! Attempting cleanup...`);
        handleStorageQuotaExceeded(word, data);
      } else {
        console.error(`❌ [SESSION STORAGE] Failed to cache "${word}":`, error);
      }
    }
  };

  const handleStorageQuotaExceeded = (newWord, newData) => {
    try {
      // Strategy 1: Clear old translation entries (keep most recent)
      const translationKeys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('translation_')) {
          translationKeys.push(key);
        }
      }

      console.log(`🧹 [CLEANUP] Found ${translationKeys.length} cached translations`);

      // Remove oldest 25% of cached translations
      const keysToRemove = translationKeys.slice(0, Math.floor(translationKeys.length * 0.25));
      keysToRemove.forEach(key => {
        sessionStorage.removeItem(key);
        console.log(`🗑️ [CLEANUP] Removed old cache: ${key}`);
      });

      // Try to cache the new word again
      const serializedData = JSON.stringify(newData);
      sessionStorage.setItem(`translation_${newWord}`, serializedData);
      console.log(`✅ [CLEANUP] Successfully cached "${newWord}" after cleanup`);

    } catch (retryError) {
      console.error(`❌ [CLEANUP] Still failed after cleanup:`, retryError);
      // Fallback: Only use memory cache for this session
      console.log(`🔄 [FALLBACK] Using memory-only cache for "${newWord}"`);
    }
  };
  const handleMouseEnter = (word) => {
    hoveredWord.current = word;

    hoverTimeout.current = setTimeout(() => {
      if (hoveredWord.current === word) {
        setDelayedHoveredWord(word);
        handleWordClick(word, true);
      }
    }, 500);
  };

  const handleMouseLeave = () => {
    hoveredWord.current = null;
    setDelayedHoveredWord(null);
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
      hoverTimeout.current = null;
    }
  };

  const fetchStudentVocabularyEntries = async () => {
    let url = `/api/v1/student-vocabulary/?video=${lesson.video.id}`;
    let allResults = [];

    try {
      while (url) {
        const response = await axios.get(url, { withCredentials: true });
        console.log("response", response);

        if (response.data?.results) {
          allResults = [...allResults, ...response.data.results];
        }

        if (response.data?.next) {
          const nextUrl = new URL(response.data.next);
          url = nextUrl.pathname + nextUrl.search;
        } else {
          url = null;
        }
      }

      setStudentVocabularyEntries(allResults);
      fetchTranslationsForEntries(allResults);

      setLoading(false);
    } catch (error) {
      console.error("Помилка отримання доданих слів:", error);
    }
  };
  const handleDeleteWord = async (entryId, text) => {
    console.log("entryId", entryId);
    console.log("text", text);

    try {
      await handleAddOrRemoveVocabulary(entryId, text);
    } catch (err) {
      console.error("Помилка при видаленні слова:", err);
    }
  };
  useEffect(() => {
    fetchStudentVocabularyEntries();

    // Cleanup function to clear caches when component unmounts
    return () => {
      console.log('[CLEANUP] Clearing translation caches');
      translationCache.current.clear();
      activeRequests.current.clear();
      requestQueue.current.clear();
    };
  }, []);
  useEffect(() => {
    setWordCount(studentVocabularyEntries.length);
  }, [studentVocabularyEntries]);

  // Handle language changes - clear translation map and memory cache
  useEffect(() => {
    console.log(`🌐 [LANGUAGE CHANGE] Language changed to: ${i18n.language}`);
    // Clear the translation map so hover tooltips will be refreshed
    setTranslationMap({});
    // Clear memory cache since it doesn't include language in the key
    translationCache.current.clear();
    // Clear any pending requests since they're for the old language
    activeRequests.current.clear();
    requestQueue.current.clear();
  }, [i18n.language]);
  const handleAddOrRemoveVocabulary = async (entryId, text) => {
    const csrfToken = getCSRFToken();
    if (!csrfToken) {
      console.error("CSRF токен відсутній");
      return;
    }
    try {
      const existingEntry = studentVocabularyEntries.find(
        (item) =>
          item.entry === entryId ||
          item.text.trim().toLowerCase() === text.trim().toLowerCase()
      );

      if (existingEntry) {
        console.log("existingEntry", existingEntry);

        const deleteResponse = await axios.delete(
          `/api/v1/student-vocabulary/${existingEntry.id}/`,
          {
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );

        setStudentVocabularyEntries((prev) =>
          prev.filter(
            (item) => item.entry !== entryId && item.id !== existingEntry.id
          )
        );

        return { type: "deleted", data: deleteResponse.data };
      } else {
        const addResponse = await axios.post(
          "/api/v1/student-vocabulary/",
          { entry: entryId },
          {
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json",
            },
            withCredentials: true,
          }
        );

        // Track first lesson opened when user adds first word
        if (lesson?.id) {
          trackLessonOpened(lesson.id);
        }

        // Check completion after adding word
        // Wait for backend to process vocabulary addition
        if (onActivityComplete) {
          setTimeout(() => {
            onActivityComplete();
          }, 2000); // Increased to 2 seconds for safety
        }

        return {
          type: "added",
          data: {
            id: addResponse.data.id,
            entry: entryId,
            text: text,
            translation: addResponse.data.translation || "Переклад відсутній",
          },
        };
      }
    } catch (error) {
      console.error(
        "Помилка додавання/видалення:",
        error.response?.data || error.message
      );
      throw error;
    }
  };

  const popupRef = useRef({
    isOpen: false,
    word: null,
    position: { x: 0, y: 0 },
    data: {},
  });
  const [popupVersion, setPopupVersion] = useState(0); // force update

  // Enhanced translation fetcher with request deduplication
  const fetchTranslation = async (word) => {
    const currentLanguage = i18n.language || 'en';
    const wordKey = `${word.toLowerCase().trim()}_${currentLanguage}`;

    // 1. Check in-memory cache first
    if (translationCache.current.has(wordKey)) {
      console.log(`[MEMORY CACHE] Found for "${word}" (${currentLanguage})`);
      return translationCache.current.get(wordKey);
    }

    // 2. Check sessionStorage cache
    const sessionCached = getCachedTranslation(word);
    if (sessionCached) {
      console.log(`[SESSION CACHE] Found for "${word}" (${currentLanguage})`);
      // Store in memory cache for faster access
      translationCache.current.set(wordKey, sessionCached);
      return sessionCached;
    }

    // 3. Check if request is already in progress
    if (activeRequests.current.has(wordKey)) {
      console.log(`[REQUEST QUEUE] Joining existing request for "${word}" (${currentLanguage})`);
      // Return a promise that resolves when the active request completes
      return new Promise((resolve, reject) => {
        if (!requestQueue.current.has(wordKey)) {
          requestQueue.current.set(wordKey, []);
        }
        requestQueue.current.get(wordKey).push({ resolve, reject });
      });
    }

    // 4. Start new API request
    console.log(`[API REQUEST] Starting new request for "${word}" (${currentLanguage})`);
    const requestPromise = new Promise(async (resolve, reject) => {
      try {
        const csrfToken = getCSRFToken();
        if (!csrfToken) {
          throw new Error("CSRF токен відсутній");
        }

        const response = await axios.post(
          `/api/v1/videoLesson/${lesson.id}/translate/`,
          { text: word },
          {
            headers: {
              "X-CSRFToken": csrfToken,
              "Content-Type": "application/json"
            },
            withCredentials: true
          }
        );

        if (response.data) {
          // Cache the result in both memory and session storage
          translationCache.current.set(wordKey, response.data);
          setCachedTranslation(word, response.data);

          console.log(`[API SUCCESS] Cached translation for "${word}" (${currentLanguage})`);
          resolve(response.data);
        } else {
          throw new Error("Не вдалося отримати дані");
        }
      } catch (error) {
        console.error(`[API ERROR] Failed to translate "${word}":`, error);
        reject(error);
      }
    });

    // Track the active request
    activeRequests.current.set(wordKey, requestPromise);

    try {
      const result = await requestPromise;

      // Resolve any queued requests for the same word
      if (requestQueue.current.has(wordKey)) {
        const queuedCallbacks = requestQueue.current.get(wordKey);
        queuedCallbacks.forEach(({ resolve }) => resolve(result));
        requestQueue.current.delete(wordKey);
      }

      return result;
    } catch (error) {
      // Reject any queued requests for the same word
      if (requestQueue.current.has(wordKey)) {
        const queuedCallbacks = requestQueue.current.get(wordKey);
        queuedCallbacks.forEach(({ reject }) => reject(error));
        requestQueue.current.delete(wordKey);
      }

      throw error;
    } finally {
      // Clean up active request tracking
      activeRequests.current.delete(wordKey);
    }
  };

  const handleWordClick = async (word, isHover = false, event) => {
    // Start loading new word data for non-hover clicks
    if (!isHover) {
      popupRef.current.word = word;
      popupRef.current.isOpen = true;
      setPopupVersion(v => v + 1);
      setPopupLoading(true);
      setPopupError(null);
    }

    try {
      // Fetch translation using the enhanced system
      const translationData = await fetchTranslation(word);

      // Update translation map for hover tooltips
      setTranslationMap(prev => ({
        ...prev,
        [word]: translationData.translation || "Переклад відсутній"
      }));

      if (!isHover) {
        // Update popup word data
        setWordTranslation(translationData.translation || "Переклад відсутній");
        setWordExample(translationData.usage_example || "Приклад відсутній");
        setWordType(translationData.type || null);
        setCommonMisunderstandings(translationData.common_misunderstandings || null);
        setWordDetails(translationData.details || null);
        setPopupLoading(false);

        // Handle vocabulary entry if needed
        if (translationData.id) {
          try {
            const result = await handleAddOrRemoveVocabulary(
              translationData.id,
              translationData.text || word
            );

            if (result.type === "added") {
              console.log(`✅ Added "${word}" to vocabulary`);
              addSound.play();
              setStudentVocabularyEntries(prev => {
                const filtered = prev.filter(item =>
                  item.text.trim().toLowerCase() !== (translationData.text || word).trim().toLowerCase()
                );
                return [...filtered, {
                  id: result.data.id,
                  entry: translationData.id,
                  text: translationData.text || word,
                  translation: translationData.translation || "Переклад відсутній"
                }];
              });
              setWordCount(prev => prev + 1);
              setShowPlusOne(true);
              setTimeout(() => setShowPlusOne(false), 1000);
            }
          } catch (err) {
            console.warn(`❌ Vocabulary error for "${word}":`, err);
          }
        }
      }
    } catch (error) {
      console.error(`❌ Error for "${word}":`, error);
      if (!isHover) {
        setPopupError(error.message || "Помилка при запиті");
        setPopupLoading(false);
      }
    }
  };

  /* const handleWordClick = async (word, isHover = false, event) => {
     console.log(`[CLICK] Слово: "${word}", isHover: ${isHover}`);
 
     // Only update position for non-hover clicks
     if (!isHover && event) {
       const rect = event.target.getBoundingClientRect();
       const newPosition = {
         x: rect.left,
         y: rect.bottom + 5
       };
 
       // If popup is already open, update position smoothly
       // If not, set initial position and open popup
       if (!isPopupOpen) {
         setPopupPosition(newPosition);
         setIsPopupOpen(true);
       } else {
         setPopupPosition(newPosition);
       }
     }
 
     const cached = getCachedTranslation(word);
     if (cached) {
       console.log("[CACHE] Знайдено кеш:", cached);
 
       setTranslationMap((prev) => ({
         ...prev,
         [word]: cached.translation || "Переклад відсутній",
       }));
 
       if (!isHover) {
         // Update word data without affecting popup visibility
         setSelectedWord(word);
         setWordExplanation(cached.explanation || "Пояснення відсутнє");
         setWordTranslation(cached.translation || "Переклад відсутній");
         setWordExample(cached.usage_example || "Приклад відсутній");
         setWordType(cached.type || null);
         setCommonMisunderstandings(cached.common_misunderstandings || null);
         setWordDetails(cached.details || null);
         setPopupLoading(false);
         setPopupError(null);
 
         if (cached.id) {
           try {
             const result = await handleAddOrRemoveVocabulary(
               cached.id,
               cached.text || word
             );
             console.log("[VOCAB ADD] Результат із кешу:", result);
 
             if (result.type === "added") {
               addSound.play();
               setStudentVocabularyEntries((prev) => {
                 const filtered = prev.filter(
                   (item) =>
                     item.text.trim().toLowerCase() !==
                     (cached.text || word).trim().toLowerCase()
                 );
 
                 return [
                   ...filtered,
                   {
                     id: result.data.id,
                     entry: cached.id,
                     text: cached.text || word,
                     translation: cached.translation || "Переклад відсутній",
                   },
                 ];
               });
               setWordCount((prev) => prev + 1);
               setShowPlusOne(true);
               setTimeout(() => setShowPlusOne(false), 1000);
             }
           } catch (err) {
             console.warn("[VOCAB ADD] ❌ Помилка з кешу:", err);
           }
         }
       }
 
       return;
     }
 
     if (!isHover) {
       // Always keep popup open when switching words
       setSelectedWord(word);
       setPopupLoading(true);
       setPopupError(null);
       setWordExplanation(null);
       setWordTranslation(null);
       setWordExample(null);
       setIsPopupOpen(true); // Always true on word click
     }
 
     try {
       const csrfToken = getCSRFToken();
       if (!csrfToken) {
         console.error("[CSRF] ❌ Відсутній CSRF токен");
         if (!isHover) setPopupError("CSRF токен відсутній");
         return;
       }
 
       const response = await axios.post(
         `/api/v1/videoLesson/${lesson.id}/translate/`,
         { text: word },
         {
           headers: {
             "X-CSRFToken": csrfToken,
             "Content-Type": "application/json",
           },
           withCredentials: true,
         }
       );
 
       if (response.data) {
         console.log("[TRANSLATE] ✅ Отримано дані:", response.data);
         const entryId = response.data.id;
 
         setCachedTranslation(word, response.data);
 
         setTranslationMap((prev) => ({
           ...prev,
           [word]: response.data.translation || "Переклад відсутній",
         }));
 
         if (!isHover) {
           const result = await handleAddOrRemoveVocabulary(
             entryId,
             response.data.text
           );
           console.log("[VOCAB] Результат:", result);
 
           setStudentVocabularyEntries((prev) => {
             const filtered = prev.filter(
               (item) =>
                 item.text.trim().toLowerCase() !==
                 (response.data.text || word).trim().toLowerCase()
             );
 
             return [
               ...filtered,
               {
                 id: response.data.id,
                 entry: entryId,
                 text: response.data.text,
                 translation: response.data.translation || "Переклад відсутній",
               },
             ];
           });
 
           setWordExplanation(response.data.explanation || "Пояснення відсутнє");
           setWordTranslation(response.data.translation || "Переклад відсутній");
           setWordExample(response.data.usage_example || "Приклад відсутній");
           setWordType(response.data.type || null);
           setCommonMisunderstandings(
             response.data.common_misunderstandings || null
           );
           setWordDetails(response.data.details || null);
         }
       } else {
         console.error("[TRANSLATE] ❌ Пустий response.data");
         if (!isHover) setPopupError("Не вдалося отримати дані.");
       }
     } catch (error) {
       console.error(`[ERROR] ❌ Помилка при запиті для "${word}":`, error);
       if (!isHover) setPopupError("Помилка при запиті.");
     } finally {
       if (!isHover) setPopupLoading(false);
     }
   };*/

  const fetchTranslationsForEntries = async (entries) => {
    console.log(`[FETCH TRANSLATIONS] Processing ${entries.length} entries`);

    const updatedEntries = await Promise.all(
      entries.map(async (item) => {
        try {
          // Use the enhanced fetchTranslation system to prevent duplicate requests
          const translationData = await fetchTranslation(item.text);

          return {
            ...item,
            translation: translationData?.translation || "Переклад відсутній",
          };
        } catch (error) {
          console.error(`[FETCH TRANSLATIONS] Error for "${item.text}":`, error);
          return {
            ...item,
            translation: "Переклад відсутній",
          };
        }
      })
    );

    setStudentVocabularyEntries(updatedEntries);
    console.log(`[FETCH TRANSLATIONS] Completed processing ${updatedEntries.length} entries`);
  };


  const handleListen = () => {
    if (!speechSynth || voices.length === 0) {
      console.error("Немає доступних голосів для відтворення.");
      alert("Ваш браузер не підтримує озвучування тексту.");
      return;
    }

    const utterance = new SpeechSynthesisUtterance(selectedWord);
    const selectedVoice =
      voices.find((voice) => voice.lang.startsWith("en")) || voices[0];

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    utterance.pitch = 1;
    utterance.rate = 1;
    utterance.volume = 1;

    speechSynth.cancel();
    speechSynth.speak(utterance);
  };


  const renderTranscription = () => {
    const fullText = lesson.video.transcription;
    const phrases = lesson.video.split_transcription;
    let currentIndex = 0;
    const elements = [];
    const entriesTextMap = new Set(
      studentVocabularyEntries.map((item) => item.text.trim().toLowerCase())
    );
    phrases.forEach((phrase, i) => {
      const safePhrase = phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(safePhrase, "i");
      const match = regex.exec(fullText.slice(currentIndex));

      if (match) {
        const start = currentIndex + match.index;
        const end = start + match[0].length;

        const beforeText = fullText.slice(currentIndex, start);
        const phraseText = fullText.slice(start, end);

        if (beforeText) {
          elements.push(<span key={`before-${i}`}>{beforeText}</span>);
        }

        elements.push(
          <span
            key={`phrase-${i}`}
            className={`${css.word} ${entriesTextMap.has(phraseText.trim().toLowerCase())
              ? css.learnedWord
              : ""
              }`}
            onClick={e => {
              const rect = e.target.getBoundingClientRect();
              const popupWidth = 360;
              const popupHeight = 600;
              const margin = 20;
              const headerHeight = 80;

              // Get current scroll position
              const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
              const scrollY = window.pageYOffset || document.documentElement.scrollTop;

              // Calculate absolute positions (relative to document, not viewport)
              const absoluteLeft = rect.left + scrollX;
              const absoluteTop = rect.top + scrollY;
              const absoluteBottom = rect.bottom + scrollY;
              const absoluteRight = rect.right + scrollX;

              // Calculate viewport boundaries accounting for scroll
              const viewportTop = scrollY + headerHeight + margin;
              const viewportBottom = scrollY + window.innerHeight - margin;
              const viewportLeft = scrollX + margin;
              const viewportRight = scrollX + window.innerWidth - margin;

              // HORIZONTAL POSITIONING (absolute coordinates)
              let x = absoluteLeft;

              // Check if popup would overflow right edge
              if (x + popupWidth > viewportRight) {
                // Try centering on word
                x = absoluteLeft + (rect.width / 2) - (popupWidth / 2);

                // Still overflows? Align to right edge of word
                if (x + popupWidth > viewportRight) {
                  x = absoluteRight - popupWidth;
                }

                // Still overflows? Align to right edge of viewport
                if (x + popupWidth > viewportRight) {
                  x = viewportRight - popupWidth;
                }
              }

              // Ensure popup doesn't go off left edge
              x = Math.max(x, viewportLeft);

              // VERTICAL POSITIONING (absolute coordinates)
              const spaceBelow = viewportBottom - absoluteBottom;
              const spaceAbove = absoluteTop - viewportTop;
              const viewportMiddle = scrollY + (window.innerHeight / 2);
              const wordVerticalPosition = absoluteTop + (rect.height / 2);

              let y;

              // Decide positioning based on available space and word position
              if (spaceBelow >= popupHeight) {
                // Enough space below - position below word
                const gapMultiplier = wordVerticalPosition < viewportMiddle ? 0.7 : 1.3;
                y = absoluteBottom + (15 * gapMultiplier);
              } else if (spaceAbove >= popupHeight) {
                // Not enough space below but enough above - position above word
                const gapMultiplier = wordVerticalPosition < viewportMiddle ? 1.3 : 0.7;
                y = absoluteTop - popupHeight - (15 * gapMultiplier);
              } else {
                // Not enough space in either direction - choose the side with more space
                if (spaceBelow > spaceAbove) {
                  const gapMultiplier = wordVerticalPosition < viewportMiddle ? 0.7 : 1.3;
                  y = absoluteBottom + (15 * gapMultiplier);
                } else {
                  y = viewportTop;
                }
              }

              // Final safety checks - ensure popup stays within viewport bounds
              y = Math.max(y, viewportTop);
              y = Math.min(y, viewportBottom - popupHeight);

              popupRef.current.position = { x, y };
              popupRef.current.isOpen = true;
              popupRef.current.word = phraseText;
              setPopupVersion(v => v + 1);
              setPopupLoading(true);
              setPopupError(null);
              handleWordClick(phraseText, false);
            }}
            onMouseEnter={() => handleMouseEnter(phraseText)}
            onMouseLeave={handleMouseLeave}
          >
            {phraseText}
            {delayedHoveredWord === phraseText && (
              <div className={css.wordHoverPopup}>
                <p className={css.wordTitle}>
                  {translationMap?.[phraseText] || "Переклад"}
                </p>
              </div>
            )}
          </span>
        );

        currentIndex = end;
      }
    });

    if (currentIndex < fullText.length) {
      elements.push(<span key="last">{fullText.slice(currentIndex)}</span>);
    }

    return <div className={css.transcription}>{elements}</div>;
  };
  // const renderWordDetails = (type, details) => {
  //   if (!type || !details) return null;

  //   switch (type) {
  //     case "verb":
  //       return (
  //         <div className={css.detailBlock}>
  //           <ul className={css.detailList}>
  //             <li className={css.listdet}>
  //               {t("grammar.infinitive")} — {details.base_verb}
  //             </li>
  //             <li className={css.listdet}>
  //               {t("grammar.thirdPersonSingular")} —{" "}
  //               {details.third_person_singular}
  //             </li>
  //             <li className={css.listdet}>
  //               {t("grammar.pastTense")} — {details.past_tense}
  //             </li>
  //             <li className={css.listdet}>
  //               {t("grammar.pastParticiple")} — {details.past_participle}
  //             </li>
  //           </ul>
  //         </div>
  //       );
  //     case "adjective":
  //     case "adverb":
  //       return (
  //         <div className={css.detailBlock}>
  //           <ul className={css.detailList}>
  //             <li className={css.listdet}>
  //               {t("grammar.comparative")} — {details.comparative}
  //             </li>
  //             <li className={css.listdet}>
  //               {t("grammar.superlative")} — {details.superlative}
  //             </li>
  //           </ul>
  //         </div>
  //       );
  //     case "noun":
  //       return (
  //         <div className={css.detailBlock}>
  //           <ul className={css.detailList}>
  //             <li className={css.listdet}>{details.plural_form}</li>
  //             <li className={css.listdet}>{details.singular_form}</li>
  //           </ul>
  //         </div>
  //       );
  //     default:
  //       return null;
  //   }
  // };

  // Memoize the onClose callback to prevent VocabularyPopup re-renders
  const handlePopupClose = useCallback(() => {
    popupRef.current.isOpen = false;
    setPopupVersion(v => v + 1);
  }, []);

  // Memoize popup data to prevent unnecessary re-renders
  const popupState = useMemo(() => ({
    isOpen: popupRef.current.isOpen,
    word: popupRef.current.word,
    position: popupRef.current.position,
    loading: popupLoading,
    error: popupError,
    data: {
      phonetic: wordType ? `[${wordType}]` : '',
      translation: wordTranslation,
      example: wordExample,
      mistakes: commonMisunderstandings,
      type: wordType,
      details: wordDetails
    }
  }), [popupVersion, wordType, wordTranslation, wordExample, commonMisunderstandings, wordDetails, popupLoading, popupError]);
  // Always render the popup, control visibility with prop
  return (
    <div className={css.wrapLearnWords}>
      <div className={css.wrapTwoChoose}>
        <div
          className={`${css.appWordsP} ${showTranscription ? css.activeTab : ""}`}
          onClick={() => setShowTranscription(true)}
        >
          <p className={css.wordAmount}>{lesson.video.split_transcription.length}</p>
          <p className={css.wordAmountText}>{t("Total Words")}</p>
        </div>
        <div
          className={`${css.appWordsP} ${!showTranscription ? css.activeTab : ""}`}
          onClick={() => setShowTranscription(false)}
        >
          <p className={css.wordAmount}>{wordCount} {showPlusOne && <span className={css.plusOne}>+1</span>}</p>
          <p className={css.wordAmountText}>{t("Words in Dictionary")}</p>
        </div>
      </div>
      {showTranscription ? (
        lesson.video.transcription && lesson.video.split_transcription ? (
          renderTranscription()
        ) : (
          <p>ℹ️ Немає транскрипції для цього уроку.</p>
        )
      ) : loading ? (
        <p>⏳ Завантаження...</p>
      ) : error ? (
        <p className={css.error}>{error}</p>
      ) : studentVocabularyEntries.length > 0 ? (
        <ul className={css.wordList}>
          {studentVocabularyEntries.map((word, index) => (
            <li key={index} className={css.wordItem}>
              <div className={css.wrapWords}>
                <span className={css.wordIndex}>{index + 1}</span>
                <span className={css.wordInEng}>{word.text}</span>
                <FaVolumeUp
                  className={css.listenIcon}
                  onClick={() => {
                    const utterance = new SpeechSynthesisUtterance(word.text);
                    const selectedVoice =
                      voices.find((voice) => voice.lang.startsWith("en")) ||
                      voices[0];
                    if (selectedVoice) {
                      utterance.voice = selectedVoice;
                    }
                    utterance.pitch = 1;
                    utterance.rate = 1;
                    utterance.volume = 1;
                    speechSynth.cancel();
                    speechSynth.speak(utterance);
                  }}
                />{" "}
                - <span className={css.wordInUk}>{word.translation}</span>
              </div>
              <AiOutlineCloseCircle
                className={css.deleteWords}
                onClick={() => handleDeleteWord(word.entry, word.text)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p>ℹ️ Слів у словнику ще немає.</p>
      )}
      <VocabularyPopup
        word={popupState.word}
        wordData={popupState.data}
        position={popupState.position}
        visible={popupState.isOpen}
        loading={popupState.loading}
        error={popupState.error}
        onClose={handlePopupClose}
      />
    </div>
  );
};

export default WordsComponent;
